import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { TransactionSettingsService } from '@feature/dashboard/settings/transaction-settings/services/transaction-settings.service';
import { IndividualTransactionSettingsInterface } from '@feature/dashboard/settings/transaction-settings/types/transaction-settings';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { CountryInfo } from '@sharedModule/models/country.model';
import { loadDestinationCountry } from '@sharedModule/store/destination-country/destination-country.actions';
import { selectDestinationCountry } from '@sharedModule/store/destination-country/destination-country.selector';
import { selectSourceCountry } from '@sharedModule/store/source-country/source-country.selector';
import { getCountryInfoFromCountryCode } from '@sharedModule/utils/array.utils';
import { Observable, Subject, takeUntil } from 'rxjs';
import { VelocityLimitService } from '../../services/velocity-limit.service';
import {
  AddVelocityLimitInterface,
  UpdateVelocityLimitInterface,
} from '../../types/upsert-velocity-limit';
import { VelocityLimitListInterface } from '../../types/velocity-limit-list';

@Component({
  selector: 'app-upsert-velocity-limit',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
  ],
  templateUrl: './upsert-velocity-limit.component.html',
  styleUrl: './upsert-velocity-limit.component.scss',
})
export class UpsertVelocityLimitComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.velocityLimit;
  upsertVelocityForm!: FormGroup;
  sourceCountryList!: Observable<CountryInfo[]>;
  destCountryList!: Observable<CountryInfo[]>;
  isEdit!: boolean;
  velocityData!: VelocityLimitListInterface;
  transferTypeList!: IndividualTransactionSettingsInterface[];
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private velocityLimitService: VelocityLimitService,
    private messageHandlerService: MessageHandlerService,
    private transactionSettingsService: TransactionSettingsService
  ) {}

  ngOnInit(): void {
    this.upsertVelocityForm = new FormGroup({
      [this.attrConstant.srcCountry.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.destCountry.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.sourceCurrency.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.destinationCurrency.key]: new FormControl('', [
        Validators.required,
      ]),
    });
    this.isEdit = this._isEdit(this.router.url);
    this.sourceCountryList = this.store.select(selectSourceCountry);
  }

  onSourceCountryChange(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    this.getDestCountry(event.value);
    const selectedSourceCountry = getCountryInfoFromCountryCode(
      event.value,
      event.selectOptions
    );
    if (selectedSourceCountry)
      this.upsertVelocityForm.patchValue({
        [this.attrConstant.sourceCurrency.key]:
          selectedSourceCountry.currencySupported[0].currency,
      });
  }

  getDestCountry(value: string): void {
    this.store.dispatch(
      loadDestinationCountry({ payload: { src_country: value } })
    );
    this.destCountryList = this.store.select(selectDestinationCountry);
  }

  onDestCountryChange(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    const selectedDestCountry = getCountryInfoFromCountryCode(
      event.value,
      event.selectOptions
    );
    if (selectedDestCountry)
      this.upsertVelocityForm.patchValue({
        [this.attrConstant.destinationCurrency.key]:
          selectedDestCountry.currencySupported[0].currency,
      });
    this.getPartnerTransactionSettings();
  }

  private _isEdit = (url: string): boolean => {
    const endUrl: string[] = url.split('/');
    if (endUrl[3] == 'edit') {
      this.getVelocityLimitDetails(+endUrl[4]);
      return true;
    } else {
      return false;
    }
  };

  getVelocityLimitDetails(id: number): void {
    this.velocityLimitService
      .getVelocityLimitDetails({ id })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            // Retain the row so update can send its id (submit fails otherwise).
            this.velocityData = x.response;
            // Destination options must load before the dest_country patch below
            // can resolve against them.
            this.getDestCountry(x.response.source_country);
            this.upsertVelocityForm.patchValue({
              [this.attrConstant.srcCountry.key]: x.response.source_country,
              [this.attrConstant.destCountry.key]:
                x.response.destination_country,
              [this.attrConstant.sourceCurrency.key]:
                x.response.source_currency,
              [this.attrConstant.destinationCurrency.key]:
                x.response.destination_currency,
            });
            // The per-transfer-type limit controls only exist once the corridor's
            // transfer types are known, so seed them from the same record.
            this.getPartnerTransactionSettings(x.response);
          } else this.redirectTo();
        },
      });
  }

  getPartnerTransactionSettings(data?: VelocityLimitListInterface): void {
    const formValue = this.upsertVelocityForm.value;
    if (formValue.src_country !== '' && formValue.dest_country !== '')
      this.transactionSettingsService
        .getPartnerTransactionSettings({
          src_country: formValue.src_country,
          dest_country: formValue.dest_country,
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.transferTypeList = x.response.transfer_types;
              for (const key in this.transferTypeList) {
                // bank-transfer -> bank_transfer, matching the record's columns.
                const name = this.transferTypeList[key].value.replace('-', '_');
                this.upsertVelocityForm.addControl(
                  name,
                  // A corridor configures only some transfer types, so a missing
                  // column seeds blank rather than null.
                  new FormControl(data?.[name] ?? '')
                );
              }
            }
          },
        });
  }

  onSubmit(): void {
    if (this.upsertVelocityForm.valid) {
      const formData = this.upsertVelocityForm.getRawValue();
      if (this.isEdit) this.updateVelocityLimit(formData);
      else this.addVelocityLimit(formData);
    }
  }

  addVelocityLimit(formValue: AddVelocityLimitInterface): void {
    this.velocityLimitService
      .addVelocityLimit(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) this.onUpsertDestCountrySuccess(x.message);
        },
      });
  }

  updateVelocityLimit(formValue: UpdateVelocityLimitInterface): void {
    this.velocityLimitService
      .updateVelocityLimit({ ...formValue, id: this.velocityData.id })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) this.onUpsertDestCountrySuccess(x.message);
        },
      });
  }

  onUpsertDestCountrySuccess(message: string): void {
    this.messageHandlerService.show(message);
    this.redirectTo();
  }

  redirectTo(): void {
    this.router.navigate(['/settings/velocity-limit/list']);
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
